<template>
    <div>
        <div class="container gy-5">
            <div class="row align-items-center justify-content-start my-4">
                <div class="flex-grow-0 col" v-if="canCreateUser">
                    <el-button type="primary" icon="Plus" @click="openCreateUserDialog">Create user</el-button>
                </div>
                <div class="flex-grow-1 col">
                    <el-input v-model="settings.query" placeholder="email or name" @input="queryChanged">
                        <template #prepend>Search by</template>
                    </el-input>
                </div>
                <div class="col flex-grow-1 col">
                    <el-pagination
                        v-model:current-page="currentPage"
                        v-model:page-size="settings.perPage"
                        :total="totalCount"
                        :page-sizes="settings.perPageOptions"
                        layout="prev, pager, next, sizes, total"
                        @size-change="pageSizeChanged"
                        @update:current-page="refreshTable"
                        background
                        style="justify-content: end"
                    />
                </div>
            </div>
        </div>
        <el-table stripe :data="displayedUsers" style="width: 100%" table-layout="auto" v-loading="usersLoading">
            <el-table-column label="Actions" v-if="canEditUsers">
                <template #default="scope">
                    <el-tooltip content="Edit user" placement="top" :enterable="false">
                        <el-button icon="Edit" circle type="primary" plain @click="openEditUserDialog(scope.row)" />
                    </el-tooltip>
                    <el-tooltip content="Reset password" placement="top" :enterable="false">
                        <el-button icon="Unlock" circle type="primary" plain @click="confirmResetPassword(scope.row)" />
                    </el-tooltip>
                </template>
            </el-table-column>
            <el-table-column label="Status">
                <template #default="scope">
                    <div class="me-2 d-inline">
                        <el-tooltip content="Account setup completed" v-if="scope.row.registered" :enterable="false">
                            <el-text size="large" type="success"><el-icon size="large"><User /></el-icon></el-text>
                        </el-tooltip>
                        <el-tooltip content="Account setup not completed" v-else-if="scope.row?.registered !== undefined" :enterable="false">
                            <el-text size="large" type="danger"><el-icon size="large"><User /></el-icon></el-text>
                        </el-tooltip>
                        <el-tooltip content="Account setup status unknown" v-else :enterable="false">
                            <el-text size="large" type="info"><el-icon size="large"><User /></el-icon></el-text>
                        </el-tooltip>
                    </div>
                    <div class="me-2 d-inline">
                        <el-tooltip content="Account enabled" v-if="scope.row.enabled" :enterable="false">
                            <el-text size="large" type="success"><el-icon size="large"><Unlock /></el-icon></el-text>
                        </el-tooltip>
                        <el-tooltip content="Account disabled" v-else-if="scope.row?.enabled !== undefined" :enterable="false">
                            <el-text size="large" type="danger"><el-icon size="large"><Lock /></el-icon></el-text>
                        </el-tooltip>
                        <el-tooltip content="Account status unknown" v-else :enterable="false">
                            <el-text size="large" type="info"><el-icon size="large"><Lock /></el-icon></el-text>
                        </el-tooltip>
                    </div>
                </template>
            </el-table-column>
            <el-table-column label="Email" prop="email" />
            <el-table-column label="Name" >
                <template #default="scope">
                    {{scope.row.first_name}} {{scope.row.last_name}}
                </template>
            </el-table-column>
            <el-table-column label="Roles" prop="roles">
                <template #default="scope">
                    <span v-for="role in scope.row.roles" :key="role.id">
                        {{role.name}} <br />
                    </span>
                </template>
            </el-table-column>
        </el-table>
        <el-row class="my-5" justify="center">
            <el-pagination
                v-model:current-page="currentPage"
                v-model:page-size="settings.perPage"
                :total="totalCount"
                :page-sizes="settings.perPageOptions"
                layout="prev, pager, next, sizes, total"
                @size-change="pageSizeChanged"
                @update:current-page="refreshTable"
                background
                style="justify-content: end"
            />
        </el-row>
        <EditUserDialog v-model:dialog="editUserDialogVisible" :user="selectedUser" width = "800px" @created="refreshTable" @edited="refreshTable" />
        <el-dialog v-model="resetPasswordDialogVisibility" title="Edit user" width="800px" @closed="selectedUser = undefined">
            <p>
                <el-icon><Warning color="danger" /></el-icon>Are you sure you want to reset the password for <strong>{{selectedUser.first_name}} {{selectedUser.last_name}}</strong>
                <span v-if="selectedUser.email"> ({{selectedUser.email}})</span>?
            </p>
            <template #footer>
                <div class="dialog-footer">
                    <el-button @click="resetPasswordDialogVisibility = false">Cancel</el-button>
                    <el-button type="primary" @click="resetPassword()">
                        Confirm
                    </el-button>
                </div>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import api from '@/lib/api'

import {mapGetters} from 'vuex'

import { ElMessageBox } from 'element-plus'

import EditUserDialog from '@/components/user/EditUserDialog.vue'

export default {
    name: 'EditUsers',
    props: {
        admin: { type: Boolean, default: false }
    },
    components: { EditUserDialog },
    data: () => {
        return {
            displayedUsers: [],
            pages: undefined,
            currentPage: 1,
            totalCount: undefined,
            usersLoading: false,
            settings: {
                perPageOptions: [10, 20, 50, 100],
                perPage: 50,
                query: ''
            },
            refreshTimeout: null,
            canCreateUser: false,
            canEditUsers: false,
            createUserDialogVisible: false,
            editUserDialogVisible: false,
            selectedUser: undefined,
            resetPasswordDialogVisibility: false
        }
    },
    computed: {
        ...mapGetters('auth', ['userinfo']),
    },
    methods: {
        pageSizeChanged () {
            this.refreshTable()
        },
        queryChanged () {
            this.usersLoading = true

            if (this.refreshTimeout) {
                clearTimeout(this.refreshTimeout)
            }

            const refreshTable = this.refreshTable

            this.refreshTimeout = setTimeout(function() {
                refreshTable()
            }, 500)
        },
        refreshTable () {
            this.usersLoading = true

            api.user.getUsers ({
                per_page: this.settings.perPage,
                page: this.currentPage,
                search: this.settings.query
            }).then(response => {
                this.currentPage = response.data.pagination.page
                this.pages = response.data.pagination.pages
                this.displayedUsers = response.data.users
                this.totalCount = response.data.pagination.total

                this.usersLoading = false
            }).catch(error => {
                this.displayedUsers = []
                this.currentPage = 1
                this.pages = undefined
                this.totalCount = undefined

                const err = error?.response?.data?.message || error.message

                this.$notify({
                    title: 'Error',
                    message: `Could not load users: ${err}`,
                    type: 'error'
                })
            })
        },
        openEditUserDialog (user) {
            this.selectedUser = user
            this.editUserDialogVisible = true
        },
        openCreateUserDialog () {
            this.selectedUser = undefined
            this.editUserDialogVisible = true
        },
        confirmResetPassword (user) {
            ElMessageBox.confirm(
                `Are you sure you want to reset the password for <strong>${user.email}</strong>?`,
                'Please confirm',
                {
                    confirmButtonText: 'OK',
                    cancelButtonText: 'Cancel',
                    type: 'warning',
                    dangerouslyUseHTMLString: true,
                }
            ).then(() => {
                api.auth.resetPassword(user.email).then(() => {
                    this.$notify({
                        title: 'Success',
                        message: `Password reset successfully for ${user.email}`,
                        type: 'success'
                    })
                }).catch ((error) => {
                    const err = error?.response?.data?.message || error.message

                    this.$notify({
                        title: 'Error',
                        message: `Could not reset password for ${user.email}: ${err}`,
                        type: 'error'
                    })
                })

                this.resetPasswordDialogVisibility = false
            }).catch(() => {
                this.resetPasswordDialogVisibility = false
            })
        }
    },
    async mounted() {
        this.refreshTable()

        api.user.getUserCreate().then(() => {this.canCreateUser = true}).catch(() => {this.canCreateUser = false})
        api.user.getUsersEdit().then(() => {this.canEditUsers = true}).catch(() => {this.canEditUsers = false})
    }
}
</script>